import { useMemo } from "react";

import { ExclamationTriangleIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import useDamageAssessment, {
  DamageAssessmentStatus,
  IDamageAssessment,
  IDamageAssessmentPartResult,
} from "../../data/useDamageAssessment";
import useVehicleImages from "../../data/useVehicleImages";
import Modal from "../../layouts/Modal";
import { IVehicle } from "../../models/Vehicle";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import ImageCarousel from "../shared/Images/ImageCarousel";
import LoadingPulse from "../shared/LoadingPulse";
import useNotification from "../notifications/useNotifications";

const MINIMUM_PHOTOS = 4;

const isDamagedPartState = (result: IDamageAssessmentPartResult) =>
  result.partState?.toUpperCase() === "DAMAGED";

type Props = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  vehicle: IVehicle | null;
};

export default function DamageAiModal({ open, setOpen, vehicle }: Props) {
  const { t } = useTranslation("jobs");
  const { addNotification } = useNotification();
  const { assessment, startAssessment } = useDamageAssessment(vehicle?.id);
  const { vehicleImages } = useVehicleImages(vehicle?.id ?? "");

  const data: IDamageAssessment | null | undefined = assessment.data?.data;
  const photoCount = vehicleImages.data?.length ?? 0;

  const inFlight =
    !!data &&
    (data.status === DamageAssessmentStatus.Pending ||
      data.status === DamageAssessmentStatus.Processing) &&
    !data.isStale;

  const canRun = photoCount >= MINIMUM_PHOTOS && !inFlight && !startAssessment.isLoading;

  const mappedCategoryCodes = useMemo(
    () => new Set((data?.damagedCategories ?? []).map((c) => c.tractableCode.toLowerCase())),
    [data?.damagedCategories]
  );

  const unmappedDamagedResults = useMemo(
    () =>
      (data?.partResults ?? []).filter(
        (r) => isDamagedPartState(r) && !mappedCategoryCodes.has(r.tractableCode.toLowerCase())
      ),
    [data?.partResults, mappedCategoryCodes]
  );

  const resultsByCode = useMemo(() => {
    const map = new Map<string, IDamageAssessmentPartResult>();
    (data?.partResults ?? []).forEach((r) => map.set(r.tractableCode.toLowerCase(), r));
    return map;
  }, [data?.partResults]);

  const carouselImages = useMemo(
    () =>
      (data?.annotatedImages ?? []).map((image, index) => ({
        id: image.cdnUrl,
        url: image.cdnUrl,
        fileName: t("Damage image {{number}}", { number: index + 1 }),
      })),
    [data?.annotatedImages, t]
  );

  const runAssessment = async () => {
    try {
      const response = await startAssessment.mutateAsync();
      if (!response.success) {
        addNotification({ primaryText: response.message, variant: "error" });
        return;
      }
      addNotification({ primaryText: t("Damage analysis started"), variant: "success" });
    } catch (error: any) {
      addNotification({
        primaryText: t("Damage analysis failed to start"),
        secondaryText: error?.message,
        variant: "error",
      });
    }
  };

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-6xl">
      <div className="space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 pr-8">
          <div>
            <h3 className="flex items-center gap-x-2 text-lg font-semibold text-slate-900">
              <SparklesIcon className="h-5 w-5 text-purple-600" />
              {t("Wilma Damage AI")}
            </h3>
            <p className="text-xs text-slate-500">{t("Powered by Tractable")}</p>
          </div>
          <button
            type="button"
            onClick={runAssessment}
            disabled={!canRun}
            className="inline-flex items-center gap-2 rounded-md bg-purple-700 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-purple-800 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            <SparklesIcon className={classNames("h-4 w-4", (inFlight || startAssessment.isLoading) && "animate-pulse")} />
            {data ? t("Re-run Damage Analysis") : t("Run Damage Analysis")}
          </button>
        </div>

        {photoCount < MINIMUM_PHOTOS && !inFlight && (
          <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
            {t("At least {{minimum}} vehicle photos are required to run damage recognition ({{count}} found).", {
              minimum: MINIMUM_PHOTOS,
              count: photoCount,
            })}
          </div>
        )}

        {assessment.isLoading && (
          <div className="py-8">
            <LoadingPulse />
          </div>
        )}

        {!assessment.isLoading && !data && photoCount >= MINIMUM_PHOTOS && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm text-slate-700">
              {t("No damage analysis has been run for this vehicle yet. {{count}} photos are ready to analyse.", {
                count: photoCount,
              })}
            </p>
          </div>
        )}

        {inFlight && (
          <div className="py-8">
            <LoadingPulse />
            <p className="mt-3 text-center text-sm text-slate-600">
              {t("Analysing {{count}} photos — this can take a few minutes...", {
                count: data?.originalImages.length ?? photoCount,
              })}
            </p>
          </div>
        )}

        {!!data && data.isStale && data.status !== DamageAssessmentStatus.Complete && data.status !== DamageAssessmentStatus.Failed && (
          <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
            {t("This analysis is taking longer than expected — Tractable may not have responded. You can re-run it.")}
          </div>
        )}

        {data?.status === DamageAssessmentStatus.Failed && (
          <div className="flex items-center gap-2 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
            {t("The damage analysis failed")}: {data.failureReason ?? t("Unknown error")}
          </div>
        )}

        {data?.status === DamageAssessmentStatus.Complete && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
            <div className="lg:col-span-7 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {t("Detected Damage")}
              </p>
              {carouselImages.length > 0 ? (
                <ImageCarousel images={carouselImages} part={true} size="w-48 h-48" />
              ) : (
                <p className="text-sm text-slate-500">{t("No damage images were produced.")}</p>
              )}
            </div>

            <div className="lg:col-span-5 space-y-3">
              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {t("Damaged Categories")}
                </p>
                <div className="mt-2 space-y-2">
                  {(data.damagedCategories ?? []).length === 0 && unmappedDamagedResults.length === 0 && (
                    <p className="text-sm text-emerald-700">{t("No damaged panels were detected.")}</p>
                  )}

                  {(data.damagedCategories ?? []).map((category) => {
                    const result = resultsByCode.get(category.tractableCode.toLowerCase());
                    const damageTypes = Array.from(
                      new Set((result?.damageInstances ?? []).map((i) => i.damageType).filter(Boolean))
                    );
                    return (
                      <div
                        key={`${category.partCategoryId}-${category.tractableCode}`}
                        className="rounded-md border border-red-200 bg-red-50 px-3 py-2"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-red-900">{category.categoryName}</p>
                          {result?.confidence != null && (
                            <span className="text-xs font-semibold text-red-700">
                              {Math.round(result.confidence * 100)}%
                            </span>
                          )}
                        </div>
                        {damageTypes.length > 0 && (
                          <p className="mt-0.5 text-xs text-red-700">{damageTypes.join(", ")}</p>
                        )}
                      </div>
                    );
                  })}

                  {unmappedDamagedResults.map((result) => (
                    <div
                      key={result.tractableCode}
                      className="rounded-md border border-slate-300 bg-slate-100 px-3 py-2"
                      title={t("No category has this Tractable Category Name yet — map it in the category editor.")}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-slate-700">{result.tractableCode}</p>
                        <span className="rounded-full border border-slate-400 bg-slate-200 px-2 py-0.5 text-[10px] font-semibold uppercase leading-tight text-slate-700">
                          {t("Unmapped")}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {t("Analysis Details")}
                </p>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="text-slate-500">{t("Photos analysed")}</p>
                    <p className="font-semibold text-slate-900">{data.originalImages.length}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">{t("Completed")}</p>
                    <p className="font-semibold text-slate-900">
                      {formatDateTime(data.completedOn, DateTime.DATETIME_SHORT, "-")}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500">{t("Panels assessed")}</p>
                    <p className="font-semibold text-slate-900">{data.partResults.length}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">{t("Unusable panels")}</p>
                    <p className="font-semibold text-slate-900">
                      {data.partResults.filter((r) => r.partUsability?.toUpperCase() === "UNUSABLE").length}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
